import { EmptyState } from '@/components/admin/empty-state';
import * as React from 'react';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  buildQuickSearchRegistry,
  filterQuickSearchRegistry,
} from '@/components/quick-search/quick-search-registry';
import { quickSearchResultIcon } from '@/components/quick-search/quick-search-icons';
import { navigateAdmin } from '@/components/quick-search/quick-search-links';
import { requestGuardedNavigation } from '@/lib/navigation-guard-registry';
import {
  groupQuickSearchResults,
  mergeQuickSearchResults,
  resolveQuickSearchGroupOrder,
} from '@/components/quick-search/quick-search-merge';
import {
  getQuickSearchResultCommandValue,
  resolveFirstVisibleQuickSearchResult,
} from '@/components/quick-search/quick-search-selection';
import { useQuickSearchServer } from '@/components/quick-search/use-quick-search';
import {
  QUICK_SEARCH_NAV_GROUP_ORDER,
  type QuickSearchRegistryResult,
  type QuickSearchResult,
} from '@/components/quick-search/quick-search-types';
import type { AuthUser, PermissionCatalogEntry } from '@/lib/types';
import type { ToolsVisibilityCapabilities } from '@/lib/tools-visibility';
import { Loader2 } from 'lucide-react';

type QuickSearchPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AuthUser | null;
  navEntries: PermissionCatalogEntry[];
  toolsCapabilities: ToolsVisibilityCapabilities;
};

export function QuickSearchPalette({
  open,
  onOpenChange,
  user,
  navEntries,
  toolsCapabilities,
}: QuickSearchPaletteProps) {
  const [query, setQuery] = React.useState('');
  const [activeValue, setActiveValue] = React.useState('');
  const userNavigatedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveValue('');
      userNavigatedRef.current = false;
    }
  }, [open]);

  const registry = React.useMemo(
    () =>
      buildQuickSearchRegistry({
        navEntries,
        user,
        toolsCapabilities,
      }),
    [navEntries, toolsCapabilities, user],
  );

  const filteredRegistry = React.useMemo(
    () => filterQuickSearchRegistry(registry, query),
    [query, registry],
  );

  const trimmedQuery = query.trim();
  const serverState = useQuickSearchServer(trimmedQuery, open);
  const showEntitySearch = trimmedQuery.length >= 2;

  const mergedResults = React.useMemo(() => {
    if (!showEntitySearch) return filteredRegistry;
    return mergeQuickSearchResults({
      query: trimmedQuery,
      registryResults: filteredRegistry,
      serverResults: serverState.results,
    });
  }, [filteredRegistry, serverState.results, showEntitySearch, trimmedQuery]);

  const groupOrder = React.useMemo(() => {
    if (!showEntitySearch) return QUICK_SEARCH_NAV_GROUP_ORDER;
    return resolveQuickSearchGroupOrder(mergedResults);
  }, [mergedResults, showEntitySearch]);

  const grouped = React.useMemo(
    () => groupQuickSearchResults(mergedResults),
    [mergedResults],
  );

  const firstVisibleValue = React.useMemo(() => {
    const first = resolveFirstVisibleQuickSearchResult(groupOrder, grouped);
    return first ? getQuickSearchResultCommandValue(first) : '';
  }, [groupOrder, grouped]);

  React.useLayoutEffect(() => {
    userNavigatedRef.current = false;
  }, [trimmedQuery]);

  React.useLayoutEffect(() => {
    if (userNavigatedRef.current) return;
    setActiveValue(firstVisibleValue);
  }, [firstVisibleValue, trimmedQuery]);

  const showHighlightsOnly = !trimmedQuery;

  function handleCommandKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      userNavigatedRef.current = true;
    }
  }

  function handleItemPointerDown() {
    userNavigatedRef.current = true;
  }

  function selectResult(result: QuickSearchResult | QuickSearchRegistryResult) {
    onOpenChange(false);
    requestGuardedNavigation(() => navigateAdmin(result.href));
  }

  function renderResultItem(result: QuickSearchResult | QuickSearchRegistryResult) {
    const Icon = quickSearchResultIcon(result.type, result.id);
    return (
      <CommandItem
        key={result.id}
        value={getQuickSearchResultCommandValue(result)}
        keywords={
          'keywords' in result
            ? [result.title, result.subtitle ?? '', ...result.keywords]
            : [result.title, result.subtitle ?? '']
        }
        onPointerDown={handleItemPointerDown}
        onSelect={() => selectResult(result)}
      >
        <Icon className="text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">{result.title}</span>
        {result.subtitle ? (
          <span className="max-w-[45%] truncate text-xs text-muted-foreground">{result.subtitle}</span>
        ) : null}
      </CommandItem>
    );
  }

  const hasVisibleResults = mergedResults.length > 0;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Quick search"
      description="Jump to admin pages, tools, configuration, and core entities."
      className="max-w-xl"
      showCloseButton
    >
      <Command
        shouldFilter={false}
        value={activeValue}
        onValueChange={setActiveValue}
        onKeyDown={handleCommandKeyDown}
      >
        <CommandInput
          placeholder="Search pages, tools, settings, sites, contacts, SOPs…"
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {showEntitySearch && serverState.loading ? (
            <p className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Searching entities…
            </p>
          ) : null}

          {showEntitySearch && serverState.error ? (
            <p className="px-3 py-2 text-xs text-muted-foreground" role="status">
              Entity search is temporarily unavailable. Page and tool results below still work.
            </p>
          ) : null}

          {showHighlightsOnly ? (
            <CommandEmpty>
              <div className="space-y-2 px-2">
                <p className="text-sm text-muted-foreground">
                  Search pages, tools, settings, sites, dealers, contacts, and SOPs.
                </p>
                <p className="text-xs text-muted-foreground">
                  Start typing to search.
                </p>
              </div>
            </CommandEmpty>
          ) : !hasVisibleResults ? (
            <CommandEmpty>
              <div className="space-y-2 px-2">
                <EmptyState variant="inline" title="No matching destinations." />
                <p className="text-xs text-muted-foreground">
                  Try a site name, contact name, SOP name, page like &quot;sites&quot;, or a tool name.
                </p>
              </div>
            </CommandEmpty>
          ) : (
            groupOrder.map((group) => {
              const items = grouped.get(group) ?? [];
              if (!items.length) return null;
              return (
                <CommandGroup key={group} heading={group}>
                  {items.map((result) => renderResultItem(result))}
                </CommandGroup>
              );
            })
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
